import { useState, type FormEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { submitReview, submitReferral } from '../lib/bridge';
import { Button } from '../components/ui/Button';
import { ErrorState } from '../components/ui/ErrorState';
import { Card, CardHeader } from '../components/ui/Card';
import { fieldControlSmClass } from '../components/ui/forms';

// Review-request and referral submission — the last piece of §9.3's
// "Customer / Payment / Follow-up" step, Field Lead only. Every submit
// goes straight through the unmodified appSubmitReviewForVerification/
// appSubmitReferralLead — the review-eligibility window, the "already
// submitted" guard, and the referral required-fields/permission-to-contact
// checks all stay exactly as they are.
export function FollowUpPanel({ jobId, reviewEligible }: { jobId: string; reviewEligible: boolean }) {
  const queryClient = useQueryClient();
  const [reviewUrl, setReviewUrl] = useState('');
  const [reviewerName, setReviewerName] = useState('');
  const [platform, setPlatform] = useState('Google');

  const [showReferral, setShowReferral] = useState(false);
  const [refName, setRefName] = useState('');
  const [refPhone, setRefPhone] = useState('');
  const [refEmail, setRefEmail] = useState('');
  const [refAddress, setRefAddress] = useState('');
  const [refWork, setRefWork] = useState('');
  const [refNotes, setRefNotes] = useState('');
  const [refConsent, setRefConsent] = useState(false);

  function invalidate() {
    void queryClient.invalidateQueries({ queryKey: ['jobDetail'] });
  }

  const reviewMutation = useMutation({
    mutationFn: () => submitReview({ jobId, platform, reviewerName, reviewUrl }),
    onSuccess: (result) => {
      if (result.ok) {
        setReviewUrl('');
        setReviewerName('');
        invalidate();
      }
    },
  });

  const referralMutation = useMutation({
    mutationFn: () => submitReferral({ sourceJobId: jobId, customerName: refName, phone: refPhone, email: refEmail, address: refAddress, requestedWork: refWork, notes: refNotes, permissionToContact: refConsent }),
    onSuccess: (result) => {
      if (result.ok) {
        setRefName('');
        setRefPhone('');
        setRefEmail('');
        setRefAddress('');
        setRefWork('');
        setRefNotes('');
        setRefConsent(false);
        setShowReferral(false);
      }
    },
  });

  return (
    <Card>
      <CardHeader title="Follow-up" />

      {reviewEligible && (
        <form
          className="mb-4 space-y-2"
          onSubmit={(e: FormEvent) => {
            e.preventDefault();
            if (reviewUrl.trim()) reviewMutation.mutate();
          }}
        >
          <p className="text-xs font-medium text-slate-400">Ask the customer for a review, then submit it here for the review bonus.</p>
          <div className="flex flex-wrap items-end gap-2">
            <select className={fieldControlSmClass} value={platform} onChange={(e) => setPlatform(e.target.value)}>
              {['Google', 'Yelp', 'Facebook', 'Other'].map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </select>
            <input className={`min-w-[10rem] flex-1 ${fieldControlSmClass}`} placeholder="Review link" value={reviewUrl} onChange={(e) => setReviewUrl(e.target.value)} />
            <input className={`w-40 ${fieldControlSmClass}`} placeholder="Reviewer name" value={reviewerName} onChange={(e) => setReviewerName(e.target.value)} />
            <Button type="submit" busy={reviewMutation.isPending} busyLabel="Submitting…">
              Submit Review
            </Button>
          </div>
          {reviewMutation.data && !reviewMutation.data.ok && <ErrorState message={reviewMutation.data.message || 'Could not submit this review.'} />}
          {reviewMutation.data?.ok && <p className="text-sm text-emerald-300">Submitted for Manager verification.</p>}
        </form>
      )}

      {!showReferral ? (
        <Button type="button" variant="secondary" onClick={() => setShowReferral(true)}>
          Submit a Referral
        </Button>
      ) : (
        <form
          className="space-y-2"
          onSubmit={(e: FormEvent) => {
            e.preventDefault();
            referralMutation.mutate();
          }}
        >
          <div className="grid gap-2 sm:grid-cols-2">
            <input className={fieldControlSmClass} placeholder="Customer name" value={refName} onChange={(e) => setRefName(e.target.value)} required />
            <input className={fieldControlSmClass} placeholder="Phone" value={refPhone} onChange={(e) => setRefPhone(e.target.value)} required />
            <input className={fieldControlSmClass} placeholder="Email" value={refEmail} onChange={(e) => setRefEmail(e.target.value)} />
            <input className={fieldControlSmClass} placeholder="Address" value={refAddress} onChange={(e) => setRefAddress(e.target.value)} />
          </div>
          <input className={`w-full ${fieldControlSmClass}`} placeholder="Requested work" value={refWork} onChange={(e) => setRefWork(e.target.value)} required />
          <input className={`w-full ${fieldControlSmClass}`} placeholder="Notes" value={refNotes} onChange={(e) => setRefNotes(e.target.value)} />
          <label className="flex items-center gap-2 text-sm text-slate-300">
            <input type="checkbox" checked={refConsent} onChange={(e) => setRefConsent(e.target.checked)} required />
            This person gave permission to be contacted by Portable Services.
          </label>
          {referralMutation.data && !referralMutation.data.ok && <ErrorState message={referralMutation.data.message || 'Could not submit this referral.'} />}
          <div className="flex gap-2">
            <Button type="submit" busy={referralMutation.isPending} busyLabel="Submitting…" disabled={!refConsent}>
              Submit Referral
            </Button>
            <Button type="button" variant="secondary" onClick={() => setShowReferral(false)}>
              Cancel
            </Button>
          </div>
        </form>
      )}
    </Card>
  );
}
